// @ts-check

import React, { useState, useEffect, useMemo } from 'react';
import { func } from 'prop-types';
import throttle from 'lodash/throttle';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import parse from 'autosuggest-highlight/parse';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import LocationOnIcon from '@mui/icons-material/LocationOn';

/** @type {*} autocompleteService */
const autocompleteService = { current: null };

/**
 * @component PlaceAutoCompleteTS
 * @param {Object} props
 * @param {function(PlaceType): void} props.handlePlace
 * @returns {JSX.Element}
 */
const PlaceAutoCompleteTS = ({ handlePlace }) => {
	const [inputValue, setInputValue] = useState('');
	const [options, setOptions] = useState([]);
	const [address, setAddress] = useState(null);
	const [selectedAddress, setSelectedAddress] = useState('');

	const fetch = useMemo(
		() =>
			throttle((request, callback) => {
				autocompleteService.current.getPlacePredictions(request, callback);
			}, 200),
		[],
	);

	useEffect(() => {
		let active = true;

		if (!autocompleteService.current && window.google) {
			autocompleteService.current = new window.google.maps.places.AutocompleteService();
		}

		if (!autocompleteService.current) return;

		if (inputValue === '') {
			setOptions(address ? [address] : []);
			return;
		}

		if (inputValue.length > 10) {
			fetch(
				{
					input: inputValue,
					location: new window.google.maps.LatLng(52, -113),
					radius: 300,
					componentRestrictions: { country: ['ca'] },
					types: ['geocode'],
				},
				(results) => {
					if (active) {
						let newOptions = [];
						if (address) newOptions = [address];
						if (results) newOptions = [...newOptions, ...results];
						setOptions(newOptions);
					}
				},
			);
		}

		return () => {
			active = false;
		};
	}, [address, inputValue, fetch]);

	useEffect(() => {
		const fetchPlace = async () => {
			try {
				/** @type {*} */
				const _geocodeByAddress = await geocodeByAddress(address.description);

				/** @type {*} */
				const _getLatLng = await getLatLng(_geocodeByAddress[0]);

				/** @type {PlaceType} */
				const placeData = { geoCode: _geocodeByAddress, latLng: _getLatLng };
				handlePlace(placeData);
				setAddress(null);
			} catch (error) {
				console.log('CATCH ERROR :>> ', error);
			}
		};
		if (address) {
			fetchPlace();
		}
	}, [address, handlePlace]);

	return (
		<Autocomplete
			getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
			filterOptions={(x) => x}
			options={options}
			autoComplete
			includeInputInList
			filterSelectedOptions
			value={selectedAddress}
			noOptionsText="No address or not found"
			onChange={(event, newValue) => {
				setOptions(newValue ? [newValue, ...options] : options);
				setAddress(newValue);
				setSelectedAddress(newValue);
			}}
			onInputChange={(event, newInputValue) => {
				setInputValue(newInputValue);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Search Address"
					InputLabelProps={{ shrink: true }}
					fullWidth
					helperText="Type at least 11 letters to activate automatic address search."
				/>
			)}
			renderOption={(props, option) => {
				const matches = option.structured_formatting.main_text_matched_substrings;
				const parts = parse(
					option.structured_formatting.main_text,
					matches.map((match) => [match.offset, match.offset + match.length]),
				);

				return (
					<li {...props}>
						<Grid container alignItems="center">
							<Grid item>
								<Box component={LocationOnIcon} sx={{ color: 'text.secondary', mr: 2 }} />
							</Grid>
							<Grid item xs>
								{parts.map((part, index) => (
									<span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
										{part.text}
									</span>
								))}
								<Typography variant="body2" color="text.secondary">
									{option.structured_formatting.secondary_text}
								</Typography>
							</Grid>
						</Grid>
					</li>
				);
			}}
		/>
	);
};

PlaceAutoCompleteTS.propTypes = { handlePlace: func.isRequired };

export default PlaceAutoCompleteTS;
